import React, { useMemo } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'

import { CONTACT_UPDATE_SUPPORTED_INTEGRATIONS, IntegrationServiceId } from '../../../common/integrations'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'

import buttonStyles from '../../../components/Button/Button.module.scss'
import { UserAvatar } from '../../../components/UserAvatar/UserAvatar'
import { Divider } from '../../../components/Divider/Divider'

import { ExternalUserServiceIntegration, User } from '../../../models/User'
import { selectContactsForThread, selectEmailCurrentUser } from '../../../store/emailHub/emailUsers'
import { useAppSelector } from '../../../store/hooks'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { IntegrationExportButton } from '../../../components/IntegrationExportButton/IntegrationExportButton'

export interface EmailThreadContactsProps {
  threadId: string
}

export const EmailThreadContacts: React.FC<EmailThreadContactsProps> = ({ threadId }) => {
  const contactsForThread = useAppSelector(selectContactsForThread(threadId))

  return (
    <div className='p-4 zocks-border-0.5 border d-flex flex-column gap-4 zocks-bg-color-white'>
      <div className='d-flex align-items-center justify-content-between'>
        <span className='paragraph-large bold'>Contacts</span>
      </div>
      <div className='d-flex flex-column gap-4'>
        {contactsForThread.map((contact, index) => (
          <React.Fragment key={contact.id}>
            <EmailThreadContactRow threadId={threadId} contact={contact} />
            {index !== contactsForThread.length - 1 ? <Divider /> : null}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

interface EmailThreadContactRowProps {
  threadId: string
  contact: User
}

const EmailThreadContactRow: React.FC<EmailThreadContactRowProps> = ({ threadId, contact }) => {
  const navigate = useNavigate()
  const { tracked } = useTracking()

  const currentUser = useAppSelector(selectEmailCurrentUser)

  const contactUpdateSupportedUserIntegrations = useMemo(
    () => currentUser?.integrations?.filter(({ serviceId }) => CONTACT_UPDATE_SUPPORTED_INTEGRATIONS.includes(serviceId as IntegrationServiceId)) ?? [],
    [currentUser],
  )

  // const _handleClientProfileButtonClicked = () => {
  //   navigate(`modal/client-profile/contacts/${contact.id}`)
  // }

  // const handleClientProfileButtonClicked = tracked(_handleClientProfileButtonClicked, {
  //   eventName: TrackEvent.EMAIL_CLIENT_PROFILE_CHANGES_OPENED,
  //   eventDetails: { threadId, userId: contact.id },
  // })

  const _handleContactUpdateButtonClicked = (integration: ExternalUserServiceIntegration) => {
    navigate(`modal/contact-update/contacts/${contact.id}/connectors/${integration.serviceId}`)
  }

  const handleContactUpdateButtonClicked = tracked(_handleContactUpdateButtonClicked, {
    eventName: TrackEvent.EMAIL_CONTACT_UPDATE_OPENED,
    eventDetails: { threadId, userId: contact.id },
  })

  return (
    <div className='d-flex align-items-center justify-content-between gap-3 flex-wrap'>
      <div className='d-flex align-items-center gap-3'>
        <UserAvatar
          width='1.5rem'
          height='1.5rem'
          fontSize='0.75rem'
          className='zocks-bg-color-gray-900 zocks-text-white'
          useInitials={true}
          initialLetters={contact.name ? contact.name[0] : ''}
        />
        <NavLink
          className={`${buttonStyles.button} ${buttonStyles.linkButton} button-link-medium `}
          to={`/library/manage-contacts/contacts/${contact.id}`}
          target='_blank'
        >
          <div className='paragraph-medium-extended bold text-truncate mw-100'>
            <div className='text-truncate'>{getUserDisplayName(contact)}</div>
          </div>
        </NavLink>
      </div>
      <div className='d-flex align-items-center gap-2'>
        {contactUpdateSupportedUserIntegrations.map((integration) => (
          <IntegrationExportButton
            key={integration.serviceId}
            serviceId={integration.serviceId}
            onClick={() => handleContactUpdateButtonClicked(integration)}
          />
        ))}
        {/*<ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} quota={{ quota: 'ai.clientProfile.value' }}>*/}
        {/*  <Button className='px-2' variant='header' onClick={handleClientProfileButtonClicked}>*/}
        {/*    <span className='paragraph-small bold'>View changes</span>*/}
        {/*  </Button>*/}
        {/*</ScopedBlock>*/}
      </div>
    </div>
  )
}
